import pRetry from "p-retry";
import axios, { detachedFromTab } from "../config/axios";
import { credentialDeadline, isUsableDeadline } from "../contexts/tabCompanySession";
import type { ResourceAccess } from "../access/resourceAccess";
import type { CompanySessionResponse } from "../shared/interfaces";
import { refusalInfo } from "../utils/admissionRefusal";

/** Backoff and polling knobs; tests shrink them. The outage poll doubles from `pollMs` to
 *  `maxPollMs`. */
export const admissionTiming = {
    initialBackoffMs: 250,
    renewalBackoffMs: 1000,
    maxRetryAfterMs: 5000,
    pollMs: 20_000,
    maxPollMs: 5 * 60_000,
};

/** A company-session answer. `expires_in` is the seconds left on `session_jwt` by the server's
 *  clock; a server that predates it sends only the absolute `exp`. `project_access` is the body
 *  `GET /project-access/me` returns for the admitted project, from a server that sends it to a
 *  request that asks (`include_project_access`). `acting_as_staff` says the tab acts as
 *  Primepoint staff in a project whose organization does not take them in as a member; a
 *  server that predates it sends none. Kept here, not in `shared/interfaces.ts`, which is at its size limit. */
export type CompanySessionAdmission = CompanySessionResponse & {
    expires_in?: number;
    project_access?: ResourceAccess;
    acting_as_staff?: boolean;
};

export interface CompanySessionSelector {
    company_id?: string;
    company_slug?: string | null;
    project_id?: string;
    project_slug?: string;
    /** On a renewal of the admitted route only: this tab's own credential, which ai_engine
     *  refreshes instead of exchanging a new provider session. */
    tab_session_jwt?: string;
    /** Asks for `project_access` with the answer: sent only by a request whose answer can seed
     *  the access lease (a first admission, a prefetch), never by a renewal. A server that
     *  predates it ignores it. */
    include_project_access?: boolean;
}

const UUID_SELECTOR = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The selector for a `/{company}/{project}` URL: a segment that is a UUID names the id. */
export function routeSelector(route: {
    companySlug: string | null;
    projectSlug: string | null;
}): CompanySessionSelector {
    const { companySlug, projectSlug } = route;
    return {
        ...(companySlug && UUID_SELECTOR.test(companySlug)
            ? { company_id: companySlug }
            : { company_slug: companySlug }),
        ...(projectSlug
            ? UUID_SELECTOR.test(projectSlug)
                ? { project_id: projectSlug }
                : { project_slug: projectSlug }
            : {}),
    };
}

/** A network error, 429 or a gateway/service outage clears on its own; every other refusal is final. */
export function isTransientAdmissionFailure(failure: unknown): boolean {
    const { status } = refusalInfo(failure);
    return status === null || status === 429 || [502, 503, 504].includes(status);
}

function pause(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve) => {
        if (signal.aborted) return resolve();
        const done = () => {
            clearTimeout(timer);
            signal.removeEventListener("abort", done);
            resolve();
        };
        const timer = setTimeout(done, ms);
        signal.addEventListener("abort", done, { once: true });
    });
}

class SessionTransportFailure extends Error {
    constructor(readonly failure: unknown) {
        super("Project admission request failed");
    }
}

/** An admission step did not finish by its attempt's deadline. Like a network failure it has no
 *  status, so it is transient: silent while the tab's credential is good, the outage notice after. */
export class AdmissionTimedOut extends Error {
    constructor() {
        super("Project admission did not finish in time");
        this.name = "AdmissionTimedOut";
    }
}

/**
 * One admission attempt's deadline. `signal` aborts when it passes or `parent` aborts, and
 * `within` stops waiting on a step at that moment, a step that cannot be cancelled (the native
 * bridge, a request interceptor) included. `release` once the attempt is over.
 */
export function admissionDeadline(parent: AbortSignal, ms: number) {
    const controller = new AbortController();
    const endsAt = Date.now() + ms;
    const abort = () => controller.abort();
    const timer = setTimeout(abort, ms);
    parent.addEventListener("abort", abort, { once: true });
    if (parent.aborted) abort();
    return {
        signal: controller.signal,
        remainingMs: () => Math.max(1, endsAt - Date.now()),
        within<T>(step: Promise<T>): Promise<T> {
            return new Promise<T>((resolve, reject) => {
                const stop = () => reject(new AdmissionTimedOut());
                if (controller.signal.aborted) return stop();
                controller.signal.addEventListener("abort", stop, { once: true });
                step.then(resolve, reject).finally(() =>
                    controller.signal.removeEventListener("abort", stop),
                );
            });
        },
        release() {
            clearTimeout(timer);
            parent.removeEventListener("abort", abort);
        },
    };
}

/** Retries transient failures through p-retry: doubling backoff from `backoffMs`, topped up to the
 *  server's Retry-After (capped). Initial admission and renewal share it; they differ in base
 *  delay and total time. Resolves with the answer, its credential's local `deadline`, timed
 *  from when the answering attempt was sent, and when it arrived (`receivedAt`); an answer whose
 *  credential lapsed on the way (a suspended tab) is asked for again, never handed back as usable.
 *  `detached` sends it `detachedFromTab`; no other request config reaches this request, which
 *  carries the source credential. */
export async function requestCompanySession(
    selector: CompanySessionSelector,
    options: {
        timeout: number;
        retries: number;
        backoffMs: number;
        signal: AbortSignal;
        assertCurrent: () => void;
        detached?: true;
    },
) {
    const deadline = Date.now() + options.timeout;
    try {
        return await pRetry(
            async () => {
                // p-retry v4 cannot cancel its delay: fence again before sending.
                try {
                    options.assertCurrent();
                } catch (error) {
                    throw new pRetry.AbortError(error as Error);
                }
                if (options.signal.aborted || Date.now() >= deadline)
                    throw new pRetry.AbortError("Project admission was cancelled or timed out");
                const sentAt = Date.now();
                try {
                    const response = await axios.post<CompanySessionAdmission>(
                        "/api/ai/session/company-session",
                        selector,
                        {
                            ...(options.detached ? detachedFromTab() : {}),
                            timeout: Math.max(1, deadline - Date.now()),
                            signal: options.signal,
                        },
                    );
                    const receivedAt = Date.now();
                    const credential = credentialDeadline(response.data ?? { exp: NaN }, sentAt);
                    // A malformed answer is the caller's to reject; only a lapsed one is retried.
                    if (Number.isFinite(credential) && !isUsableDeadline(credential))
                        throw new Error("The project credential expired before it arrived");
                    return { ...response, deadline: credential, receivedAt };
                } catch (failure) {
                    const error = new SessionTransportFailure(failure);
                    if (options.signal.aborted || !isTransientAdmissionFailure(failure))
                        throw new pRetry.AbortError(error);
                    throw error;
                }
            },
            {
                retries: options.retries,
                minTimeout: options.backoffMs,
                factor: 2,
                maxRetryTime: options.timeout,
                onFailedAttempt: async (error) => {
                    if (!(error instanceof SessionTransportFailure)) return;
                    const { retryAfterMs } = refusalInfo(error.failure);
                    if (retryAfterMs === null || error.retriesLeft === 0) return;
                    // p-retry waits its own backoff next; add what Retry-After asks beyond it.
                    const backoff = options.backoffMs * 2 ** (error.attemptNumber - 1);
                    const extra = Math.min(retryAfterMs, admissionTiming.maxRetryAfterMs) - backoff;
                    if (extra <= 0) return;
                    if (Date.now() + backoff + extra >= deadline) throw error;
                    await pause(extra, options.signal);
                },
            },
        );
    } catch (error) {
        // Keep the exact transport detail for expired-session/MFA recovery.
        throw error instanceof SessionTransportFailure ? error.failure : error;
    }
}
